import {
  createUnionType,
  Field,
  GraphQLJSON,
  ID,
  InputAndObjectType,
  ObjectType,
  registerEnumType,
} from '@warebee/shared/util-backend-only-types';
import { IsNotEmpty, IsString } from '@warego/class-validator';
import { BuildActualityPreAggregatedTablesJobInput } from '../actuality.model';
import {
  AnalyzeActualityJobInput,
  AnalyzeActualityJobOutput,
} from '../engine-api';
import { ImportJobImportType } from '../import.model';

export enum ProcessingPipelineTriggerType {
  DATASET_CHANGED = 'DATASET_CHANGED',
  DATASET_OBJECT_CREATED = 'DATASET_OBJECT_CREATED',
}

registerEnumType(ProcessingPipelineTriggerType, {
  name: 'ProcessingPipelineTriggerType',
});

@InputAndObjectType()
export class ProcessingPipelineDatasetChangedTriggerSource {
  @Field(() => ID)
  id: string;
}

@InputAndObjectType()
export class ProcessingPipelineDatasetChangedTriggerSettings {
  @Field(() => [ProcessingPipelineDatasetChangedTriggerSource])
  sources: ProcessingPipelineDatasetChangedTriggerSource[];
}

@InputAndObjectType()
export class ProcessingPipelineDatasetObjectCreatedTriggerSource {
  @Field(() => ImportJobImportType)
  objectType: ImportJobImportType;

  @Field(() => [String], { nullable: true })
  userTagsHasAny?: string[];

  @Field(() => [String], { nullable: true })
  userTagsHasAll?: string[];
}

@InputAndObjectType()
export class ProcessingPipelineDatasetObjectCreatedTriggerSettings {
  @Field(() => [ProcessingPipelineDatasetObjectCreatedTriggerSource])
  sources: ProcessingPipelineDatasetObjectCreatedTriggerSource[];
}

@InputAndObjectType()
export class ProcessingPipelineTrigger {
  @Field(() => ProcessingPipelineTriggerType)
  type: ProcessingPipelineTriggerType;

  @Field(() => ID)
  id: string;

  @Field()
  enabled: boolean;

  @Field({ nullable: true })
  name?: string;

  @Field({ nullable: true })
  description?: string;

  @Field(() => ProcessingPipelineDatasetChangedTriggerSettings, {
    nullable: true,
  })
  datasetChanged?: ProcessingPipelineDatasetChangedTriggerSettings;

  @Field(() => ProcessingPipelineDatasetObjectCreatedTriggerSettings, {
    nullable: true,
  })
  datasetObjectCreated?: ProcessingPipelineDatasetObjectCreatedTriggerSettings;
}

@InputAndObjectType()
export class ProcessingPipelineTriggerSettings {
  @Field(() => [ProcessingPipelineTrigger], { nullable: true })
  triggers?: ProcessingPipelineTrigger[];
}

export enum ProcessingPipelineType {
  ANALYZE_ACTUALITY = 'ANALYZE_ACTUALITY',
  GENERIC = 'GENERIC',
  BUILD_ACTUALITY_PRE_AGGREGATED_TABLES = 'BUILD_ACTUALITY_PRE_AGGREGATED_TABLES',
}

registerEnumType(ProcessingPipelineType, {
  name: 'ProcessingPipelineType',
});

@InputAndObjectType()
export class AnalyzeActualityPipelineSettings {
  @Field(() => ID)
  @IsNotEmpty()
  @IsString()
  actualityId: string;
}

@InputAndObjectType()
export class BuildActualityPreAggregatedTablesPipelineSettings {
  @Field(() => ID)
  @IsNotEmpty()
  @IsString()
  actualityId: string;
}

export enum GenericPipelineParameterMappingSource {
  CONSTANT = 'CONSTANT',
  ARRAY_MAPPING = 'ARRAY_MAPPING',

  TRIGGERING_EVENT_WAREHOUSE_ID = 'TRIGGERING_EVENT_WAREHOUSE_ID',
  TRIGGERING_EVENT_OBJECT_TYPE = 'TRIGGERING_EVENT_OBJECT_TYPE',
  TRIGGERING_EVENT_OBJECT_ID = 'TRIGGERING_EVENT_OBJECT_ID',
  TRIGGERING_EVENT_IMPORT_JOB_ID = 'TRIGGERING_EVENT_IMPORT_JOB_ID',
  TRIGGERING_EVENT_IMPORT_JOB_KEY = 'TRIGGERING_EVENT_IMPORT_JOB_KEY',
  TRIGGERING_EVENT_OBJECT_CREATED = 'TRIGGERING_EVENT_OBJECT_CREATED',

  USER_ID = 'USER_ID',
}

registerEnumType(GenericPipelineParameterMappingSource, {
  name: 'GenericPipelineParameterMappingSource',
});

@InputAndObjectType()
export class BaseGenericPipelineParameterMapping {
  @Field(() => GenericPipelineParameterMappingSource, {
    nullable: true,
    defaultValue: GenericPipelineParameterMappingSource.CONSTANT,
  })
  source?: GenericPipelineParameterMappingSource;

  @Field(() => GraphQLJSON, { nullable: true })
  value?: any;

  @Field(() => [BaseGenericPipelineParameterMapping], { nullable: true })
  arrayMapping?: BaseGenericPipelineParameterMapping[];
}

@InputAndObjectType()
export class GenericPipelineParameterMapping extends BaseGenericPipelineParameterMapping {
  @Field(() => ID)
  name: string;
}

@InputAndObjectType()
export class GenericPipelineSettings {
  @Field(() => ID)
  templateId: string;

  @Field(() => [GenericPipelineParameterMapping], { nullable: true })
  parameterMapping?: GenericPipelineParameterMapping[];

  @Field({ nullable: true })
  skippable?: boolean;
}

@InputAndObjectType()
export class GenericPipelineJobInput {
  @Field(() => GraphQLJSON, { nullable: true })
  parameters?: any;
}

@ObjectType()
export class GenericPipelineJobOutput {
  @Field(() => GraphQLJSON, { nullable: true })
  output?: any;
}

export const ProcessingPipelineSettingsUnion = createUnionType({
  name: 'ProcessingPipelineSettings',
  types: () => [
    AnalyzeActualityPipelineSettings,
    BuildActualityPreAggregatedTablesPipelineSettings,
    GenericPipelineSettings,
  ],
});

export type ProcessingPipelineSettings =
  | AnalyzeActualityPipelineSettings
  | GenericPipelineSettings
  | BuildActualityPreAggregatedTablesPipelineSettings;

export const ProcessingPipelineJobInputUnion = createUnionType({
  name: 'ProcessingPipelineJobInput',
  types: () => [
    AnalyzeActualityJobInput,
    BuildActualityPreAggregatedTablesJobInput,
    GenericPipelineJobInput,
  ],
});

export type ProcessingPipelineJobInput =
  | AnalyzeActualityJobInput
  | BuildActualityPreAggregatedTablesJobInput
  | GenericPipelineJobInput;

export const ProcessingPipelineJobOutputUnion = createUnionType({
  name: 'ProcessingPipelineJobOutput',
  types: () => [AnalyzeActualityJobOutput, GenericPipelineJobOutput],
});

export type ProcessingPipelineJobOutput =
  | AnalyzeActualityJobOutput
  | GenericPipelineJobOutput;
